import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  focus,
  radius,
  semantic,
  space,
  typeRole,
  weight,
  type StatusRole,
} from '@repo/design-tokens';
import {
  Button,
  Icon,
  Kbd,
  Note,
  Progress as ProgressBar,
  Skeleton,
  Tag,
} from '@repo/design-system';
import type { IconName } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';
import {
  STATE_TEXT,
  acceptAllTargets,
  heatScoreLine,
  reasonText,
  type DiscardReason,
  type FallbackReason,
  type MachineReview,
  type ReviewMachine,
  type ReviewState,
} from './machineStateModel';
import { PART_LABEL } from './suggestionModel';

/** The optional reasons a discard can carry (spec `observer-autogen-review-discard-and-scores`, R1.4). */
const DISCARD_REASONS: { value: DiscardReason; label: string }[] = [
  { value: 'wrong-place', label: 'Wrong place' },
  { value: 'wrong-machine', label: 'Wrong machine' },
  { value: 'not-a-machine', label: 'Not a machine' },
];

/** Each state's glyph: never colour alone (R2.4). */
const STATE_ICON: Record<ReviewState, IconName> = {
  'not-watched': 'eye-slash',
  better: 'wand-magic-sparkles',
  'name-uncertain': 'circle-question',
  'no-heat': 'triangle-exclamation',
  settled: 'circle-check',
};

/**
 * Each state's status role (`onboarding-status-colours-observers`, R11), so the review's glyphs read
 * as the rest of the step's: a machine nothing watches is work to do, a doubtful name or no heat a
 * problem, a better suggestion information (Maria's ruling), and a settled one done, unmarked.
 */
export const REVIEW_STATE_ROLE: Readonly<Record<ReviewState, StatusRole | 'done'>> = {
  'not-watched': 'workToDo',
  better: 'information',
  'name-uncertain': 'problem',
  'no-heat': 'problem',
  settled: 'done',
};

/** A state's glyph ink: its role's, or `null` for done, which keeps the row's own ink. */
export const reviewGlyphInk = (state: ReviewState): string | null => {
  const role = REVIEW_STATE_ROLE[state];
  return role && role !== 'done' ? semantic.status.roles[role].ink : null;
};

const StateIcon = styled(Icon)<{ $ink: string | null }>`
  ${({ $ink }) => ($ink ? `color: ${$ink};` : '')}
`;

export const FALLBACK_TEXT: Record<FallbackReason, string> = {
  'not-calibrated': 'This camera isn’t calibrated, so machines can’t be placed on its picture.',
  'camera-skipped': 'The engine skipped this camera — showing the camera’s list instead.',
  'camera-failed': 'Suggestions failed for this camera — showing the camera’s list instead.',
};

export interface MachineListProps {
  /** The walk-through for the camera shown; null while nothing has been read. */
  review: MachineReview | null;
  loading: boolean;
  /** A failed read's sentence; the inventory is shown under it, never "nothing to review". */
  error: string | null;
  onRetry: () => void;
  /** The camera's inventory, shown when the engine can't place machines (R2.5). */
  fallbackContent: React.ReactNode;
  selectedRefid: string | null;
  onSelect: (machine: ReviewMachine) => void;
  onHover: (refid: string | null) => void;
  onUse: (machine: ReviewMachine) => void;
  onKeep: (machine: ReviewMachine) => void;
  /** Undo a Keep mine or a Discard: the machine asks again. */
  onForget: (machine: ReviewMachine) => void;
  /** Discard (D): set the suggestion aside (spec `observer-autogen-review-discard-and-scores`, R1). */
  onDiscard: (machine: ReviewMachine) => void;
  /** A discard's optional reason; null clears it (R1.4). */
  onDiscardReason: (machine: ReviewMachine, reason: DiscardReason | null) => void;
  /** Your drawn observers' body part, when they share one, for the Heat score row (R2.2). */
  drawnPart: (machine: ReviewMachine) => string | null;
  onAdjust: (machine: ReviewMachine) => void;
  onRelink: (machine: ReviewMachine, targetRefid: string) => void;
  /** Machines a region can be re-linked to: the engine's candidates first, then the rest in view. */
  relinkOptions: (machine: ReviewMachine) => { refid: string; name: string }[];
  onAcceptAll: (machines: ReviewMachine[]) => void;
  /** Which machine's re-link options are open (the L key opens them too). */
  relinkingRefid: string | null;
  onToggleRelink: (refid: string) => void;
  /** Every machine's suggestion faint behind the one under review (R3.2). */
  showAll: boolean;
  onShowAll: (on: boolean) => void;
  /**
   * Every listed machine's outline faint on the picture (*Show every machine outline*, spec
   * `observer-matching-beyond-geometry-machine-outline`).
   */
  showOutlines: boolean;
  onShowOutlines: (on: boolean) => void;
  shownUnexplainedId: string | null;
  onShowUnexplained: (id: string | null) => void;
  /** Days of heat behind the answer and the window's length, for the card's line. */
  days: { binned: number; window: number } | null;
}

/**
 * The machine-first walk-through (spec `observer-autogen-review-walkthrough`, R2, R4, R6): every
 * machine the calibration places in view, ordered by the work left, a progress bar, the selected
 * machine's comparison and its four actions, Accept all confident, and heat that matches no machine.
 */
const MachineList: React.FC<MachineListProps> = ({
  review,
  loading,
  error,
  onRetry,
  fallbackContent,
  selectedRefid,
  onSelect,
  onHover,
  onUse,
  onKeep,
  onForget,
  onDiscard,
  onDiscardReason,
  drawnPart,
  onAdjust,
  onRelink,
  relinkOptions,
  onAcceptAll,
  relinkingRefid,
  onToggleRelink,
  showAll,
  onShowAll,
  showOutlines,
  onShowOutlines,
  shownUnexplainedId,
  onShowUnexplained,
  days,
}) => {
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [confirmingAll, setConfirmingAll] = useState(false);
  const titleId = useId();
  const selectedRowRef = useRef<HTMLLIElement | null>(null);
  // N / Shift+N move the selection from the picture: bring its row and comparison into view
  // without taking focus off the picture (Copilot, #2156).
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedRefid]);

  if (error) {
    return (
      <Wrap>
        <Header>
          <Note tone="error" icon={<Icon name="triangle-exclamation" size="xs" />}>
            {`Couldn’t load suggestions — ${error}`}
          </Note>
          <Button type="button" size="sm" variant="frosted" onClick={onRetry}>
            Retry
          </Button>
        </Header>
        <Fallback>{fallbackContent}</Fallback>
      </Wrap>
    );
  }
  if (loading || !review) {
    return (
      <Wrap aria-busy="true">
        <Header>
          <span className="progress-text">Reading suggestions…</span>
        </Header>
        <SkeletonList>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </SkeletonList>
        <Fallback>{fallbackContent}</Fallback>
      </Wrap>
    );
  }
  if (review.fallback) {
    return (
      <Wrap>
        <Header>
          <Note tone="neutral" icon={<Icon name="circle-question" size="xs" />}>
            {FALLBACK_TEXT[review.fallback]}
          </Note>
        </Header>
        <Fallback>{fallbackContent}</Fallback>
      </Wrap>
    );
  }

  const { settled, inView } = review.progress;
  // The selected machine's card stays while it is discarded, so its reason and Ask again stay in
  // reach under "Needs a decision" until you move on (Copilot, #2190).
  const shown =
    filter === 'open'
      ? review.machines.filter(
          m => m.state !== 'settled' || (m.discarded && m.targetRefid === selectedRefid),
        )
      : review.machines;
  const confident = acceptAllTargets(review);

  return (
    <Wrap aria-labelledby={titleId}>
      <Header>
        <h3 id={titleId} className="title">
          Machines in view
        </h3>
        <Progress>
          {inView > 0 && (
            <ProgressBar
              value={settled}
              max={inView}
              label={`${settled} of ${inView} machines settled`}
              valueText={`${settled} of ${inView} machines settled`}
            />
          )}
          <span className="progress-text">
            {inView === 0
              ? 'The calibration places no machines on this camera’s picture.'
              : `${settled} of ${inView} settled`}
          </span>
        </Progress>
        <Filter role="group" aria-label="Which machines">
          <Tag
            surface="on-glass"
            toggle
            active={filter === 'open'}
            onToggle={() => setFilter('open')}
          >
            Needs a decision
          </Tag>
          <Tag
            surface="on-glass"
            toggle
            active={filter === 'all'}
            onToggle={() => setFilter('all')}
          >
            All
          </Tag>
          <Tag surface="on-glass" toggle active={showAll} onToggle={() => onShowAll(!showAll)}>
            Show every suggestion
          </Tag>
          <Tag
            surface="on-glass"
            toggle
            active={showOutlines}
            onToggle={() => onShowOutlines(!showOutlines)}
          >
            Show every machine outline
          </Tag>
        </Filter>
      </Header>

      <Rows>
        {shown.length === 0 && inView > 0 && (
          <li className="done">{`Camera done: all ${inView} machines settled.`}</li>
        )}
        {shown.map(m => {
          const selected = m.targetRefid === selectedRefid;
          const part = m.proposal ? PART_LABEL[m.proposal.observer.trackSide] : null;
          return (
            <li key={m.targetRefid} ref={selected ? selectedRowRef : undefined}>
              <Row
                type="button"
                data-state={m.state}
                aria-current={selected ? 'true' : undefined}
                aria-label={`${m.name}, ${STATE_TEXT[m.state]}${
                  m.discarded ? ', suggestion discarded' : part ? `, suggested ${part}` : ''
                }`}
                onClick={() => onSelect(m)}
                onMouseEnter={() => onHover(m.targetRefid)}
                onMouseLeave={() => onHover(null)}
                onFocus={() => onHover(m.targetRefid)}
                onBlur={() => onHover(null)}
              >
                <StateIcon
                  name={STATE_ICON[m.state]}
                  size="sm"
                  className="state-glyph"
                  $ink={reviewGlyphInk(m.state)}
                />
                <span className="name">{m.name}</span>
                <span className="state">
                  {STATE_TEXT[m.state]}
                  {m.suggestionDiffers ? ' · suggestion differs' : ''}
                  {m.discarded ? ' · suggestion discarded' : ''}
                  {part && m.state !== 'settled' && !m.discarded ? ` · ${part}` : ''}
                </span>
              </Row>
              {selected && (
                <Card role="region" aria-label={`Suggestion for ${m.name}`}>
                  <p className="reason">
                    {m.proposalTaken
                      ? 'This suggestion now watches another machine (Wrong machine). Undo to take it back.'
                      : reasonText(m)}
                  </p>
                  <dl>
                    {(() => {
                      const line = heatScoreLine(m, drawnPart(m), p => PART_LABEL[p]);
                      return line ? (
                        <>
                          <dt>Heat score</dt>
                          <dd>{line}</dd>
                        </>
                      ) : null;
                    })()}
                    {m.proposal && !m.discarded && (
                      <>
                        <dt>Suggested</dt>
                        <dd>
                          {`${part} · ${Math.round(m.proposal.confidence * 100)}% confidence`}
                          {days ? ` · from ${days.binned} of ${days.window} days` : ''}
                          {m.partlyInView ? ' · partly in view' : ''}
                        </dd>
                      </>
                    )}
                    {m.state === 'name-uncertain' && (m.proposal?.candidates.length ?? 0) > 0 && (
                      <>
                        <dt>Could be</dt>
                        <dd>
                          {m
                            .proposal!.candidates.map(c => c.targetName ?? c.targetRefid)
                            .join(', ')}
                        </dd>
                      </>
                    )}
                  </dl>
                  {m.discarded && (
                    <Reasons role="group" aria-label="Why was it wrong? (optional)">
                      {DISCARD_REASONS.map(r => (
                        <Tag
                          key={r.value}
                          surface="on-glass"
                          toggle
                          active={m.discardReason === r.value}
                          onToggle={() =>
                            onDiscardReason(m, m.discardReason === r.value ? null : r.value)
                          }
                        >
                          {r.label}
                        </Tag>
                      ))}
                    </Reasons>
                  )}
                  <Actions>
                    {(m.settledBy === 'kept' || m.discarded) && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => onForget(m)}>
                        Ask again
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      disabled={!m.proposal || m.proposalTaken || m.discarded}
                      onClick={() => onUse(m)}
                    >
                      Use suggestion <Kbd keys="A" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="frosted"
                      disabled={m.drawnObserverRefids.length === 0 || !m.proposal || m.discarded}
                      onClick={() => onKeep(m)}
                    >
                      Keep mine <Kbd keys="K" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="frosted"
                      disabled={!m.proposal || m.proposalTaken || m.discarded}
                      onClick={() => onAdjust(m)}
                    >
                      Adjust <Kbd keys="E" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="frosted"
                      disabled={!m.proposal || m.proposalTaken || m.discarded}
                      aria-expanded={relinkingRefid === m.targetRefid}
                      onClick={() => onToggleRelink(m.targetRefid)}
                    >
                      Wrong machine <Kbd keys="L" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={!m.proposal || m.proposalTaken || m.discarded}
                      onClick={() => onDiscard(m)}
                    >
                      Discard <Kbd keys="D" />
                    </Button>
                  </Actions>
                  {relinkingRefid === m.targetRefid && (
                    <Relink role="group" aria-label={`Link this region to another machine`}>
                      {relinkOptions(m).map(o => (
                        <Button
                          key={o.refid}
                          type="button"
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            onToggleRelink(m.targetRefid);
                            onRelink(m, o.refid);
                          }}
                        >
                          {o.name}
                        </Button>
                      ))}
                    </Relink>
                  )}
                </Card>
              )}
            </li>
          );
        })}
      </Rows>

      <Footer>
        {confirmingAll ? (
          <>
            <span>{`Use ${confident.length} confident suggestion${confident.length === 1 ? '' : 's'} for machines nothing watches?`}</span>
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => {
                setConfirmingAll(false);
                onAcceptAll(confident);
              }}
            >
              Accept {confident.length}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmingAll(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="frosted"
            disabled={confident.length === 0}
            onClick={() => setConfirmingAll(true)}
          >
            {`Accept all confident (${confident.length})`}
          </Button>
        )}
      </Footer>

      {review.unexplainedTotal > 0 && (
        <PlanError aria-label="Possible plan error">
          <h4>{`Possible plan error (${review.unexplainedTotal})`}</h4>
          <p className="hint">Busy floor with no machine on the plan — check the Floor Plan.</p>
          <ul>
            {review.unexplained.map((p, i) => {
              const id = p.observer.refid;
              const showing = shownUnexplainedId === id;
              return (
                <li key={id}>
                  <span>{`Area ${i + 1} · ${PART_LABEL[p.observer.trackSide]}`}</span>
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    aria-pressed={showing}
                    onClick={() => onShowUnexplained(showing ? null : id)}
                  >
                    {showing ? 'Hide' : 'Show on picture'}
                  </Button>
                </li>
              );
            })}
          </ul>
        </PlanError>
      )}
      <span style={visuallyHidden}>
        Keys: N next, Shift N previous, A use, K keep, E adjust, L wrong machine.
      </span>
    </Wrap>
  );
};

export default MachineList;

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  min-height: 0;
  overflow: auto;
  padding: ${space[3]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  .title {
    margin: 0;
    font-size: ${typeRole.body1Stronger.fontSize};
    line-height: ${typeRole.body1Stronger.lineHeight};
    font-weight: ${weight.semibold};
    color: ${semantic.text.primary};
  }
  .progress-text {
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.muted};
  }
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
`;

const Filter = styled.div`
  display: flex;
  /* Four toggles outgrow the 340 px navigator: wrap rather than scroll the panel sideways. */
  flex-wrap: wrap;
  gap: ${space[1]};
`;

const Rows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  .done {
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.muted};
    padding: ${space[2]};
  }
`;

const Row = styled.button`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: ${space[2]};
  align-items: center;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: ${radius.md};
  background: transparent;
  padding: ${space[2]};
  cursor: pointer;
  color: ${semantic.text.primary};
  .name {
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    font-weight: ${weight.semibold};
  }
  .state {
    grid-column: 2;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.muted};
  }
  &[aria-current='true'] {
    border-color: ${semantic.border.selected};
    background: ${semantic.surface.base};
  }
  &:hover {
    background: ${semantic.surface.base};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${focus.ring};
  }
`;

const Card = styled.div`
  margin: ${space[1]} 0 ${space[2]} ${space[5]};
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  .reason {
    margin: 0;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.secondary};
  }
  dl {
    margin: 0;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: ${space[1]} ${space[2]};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
  }
  dt {
    color: ${semantic.text.muted};
  }
  dd {
    margin: 0;
    color: ${semantic.text.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[1]};
`;

const Reasons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[1]};
`;

const Relink = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[1]};
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.secondary};
`;

const PlanError = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  h4 {
    margin: 0;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    font-weight: ${weight.semibold};
    color: ${semantic.text.primary};
  }
  .hint {
    margin: 0;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.muted};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
  }
`;

const SkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
`;

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
`;
